import { useState } from "react";
import { Save, Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Admin-only form to inject SMTP credentials directly into our Mongo
 * runtime_config collection (_id='smtp'). Workaround for the same Emergent
 * Production secrets-store persistence bug that hit Hetzner. env-vars still
 * win when present.
 */
const SmtpConfigForm = ({ onSaved }) => {
  const [form, setForm] = useState({
    host: "smtps.udag.de",
    port: "465",
    user: "",
    password: "",
    from_email: "noreply@novatusakademie.pro",
    from_name: "Novatus Sales Academy",
    use_ssl: "true",
    otp_delivery: "smtp",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.host.trim() || !form.user.trim() || !form.password.trim() || !form.from_email.trim()) {
      toast.error("Host, User, Passwort und Absender-E-Mail sind Pflicht");
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/smtp/configure", form);
      setResult(data);
      toast.success("SMTP-Werte gespeichert. Sende dir einen Test-OTP, um die Verbindung zu prüfen.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nv-card p-5 mt-4 border-novatus-green/40" data-testid="smtp-config-form">
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-novatus-green shrink-0 mt-1" />
        <div className="flex-1">
          <p className="label-eyebrow text-novatus-green">Notfall-Konfiguration</p>
          <h3 className="font-display font-bold text-white text-base mt-1">
            SMTP direkt in DB speichern
          </h3>
          <p className="text-xs text-novatus-text-2 mt-2 leading-relaxed">
            Workaround, falls die Werte im Emergent Secrets-Tab nicht persistent
            bleiben. Die Werte landen in unserer eigenen MongoDB
            (<code className="text-novatus-green">runtime_config</code>) und werden
            beim Backend-Start sowie sofort nach dem Speichern aktiviert. ENV-Vars
            haben Vorrang, wenn sie gesetzt sind.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3">
        {[
          { key: "host", label: "SMTP HOST", placeholder: "smtps.udag.de", required: true },
          { key: "port", label: "PORT", placeholder: "465", required: true },
          { key: "user", label: "USER (E-Mail-Adresse)", placeholder: "noreply@novatusakademie.pro", required: true },
          { key: "password", label: "PASSWORT", placeholder: "•••", required: true, mono: true, type: "password" },
          { key: "from_email", label: "FROM_EMAIL", placeholder: "noreply@novatusakademie.pro", required: true },
          { key: "from_name", label: "FROM_NAME", placeholder: "Novatus Sales Academy" },
        ].map((f) => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider font-bold text-novatus-text-3 block mb-1">
              {f.label} {f.required && <span className="text-amber-400">*</span>}
            </label>
            <input
              type={f.type || "text"}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={form[f.key]}
              onChange={update(f.key)}
              placeholder={f.placeholder}
              className={`w-full h-10 rounded-lg bg-novatus-card-2 border border-novatus text-white px-3 text-sm focus:border-novatus-green focus:outline-none ${f.mono ? "font-mono text-xs" : ""}`}
              data-testid={`smtp-${f.key}-input`}
            />
          </div>
        ))}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-novatus-text-3 block mb-1">
              SSL/TLS
            </label>
            <select
              value={form.use_ssl}
              onChange={update("use_ssl")}
              className="w-full h-10 rounded-lg bg-novatus-card-2 border border-novatus text-white px-3 text-sm focus:border-novatus-green focus:outline-none"
              data-testid="smtp-use_ssl-input"
            >
              <option value="true">SSL (Port 465)</option>
              <option value="false">STARTTLS (Port 587)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-novatus-text-3 block mb-1">
              MODUS
            </label>
            <select
              value={form.otp_delivery}
              onChange={update("otp_delivery")}
              className="w-full h-10 rounded-lg bg-novatus-card-2 border border-novatus text-white px-3 text-sm focus:border-novatus-green focus:outline-none"
              data-testid="smtp-otp_delivery-input"
            >
              <option value="smtp">SMTP (echte Mails)</option>
              <option value="dev">Dev (Log only)</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="btn-green w-full h-11 rounded-lg inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50 mt-2"
          data-testid="smtp-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Speichere…" : "Speichern"}
        </button>

        {result && (
          <div
            className="mt-3 rounded-lg p-3 text-xs flex items-start gap-2 bg-novatus-green/10 border border-novatus-green/40 text-novatus-green"
            data-testid="smtp-result"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Erfolg</p>
              <p className="mt-1 leading-relaxed">
                Aktualisierte Felder: {result.fields_updated?.join(", ") || "—"}
              </p>
              <p className="mt-1 leading-relaxed">
                Teste den Versand jetzt: logge dich aus und fordere einen neuen OTP an.
                Der Code muss als E-Mail ankommen.
              </p>
            </div>
          </div>
        )}
      </form>

      <p className="text-[10px] text-novatus-text-3 mt-3 leading-relaxed">
        Hinweis: Das Passwort wird verschlüsselt mit der MongoDB übertragen
        und ist nur Admins zugänglich. Sobald Emergent Support den
        Persistenz-Bug behebt, kannst du diesen DB-Eintrag entfernen.
      </p>
    </div>
  );
};

export default SmtpConfigForm;
